/**
 * Where the practice console lives in the CURRENT address — the one authority every
 * in-console link, redirect and `navigate()` goes through (SHEET-230).
 *
 * The console is mounted by two route trees:
 *   - `routes/adminRoutes.jsx` → `/admin/*` on the platform host; tenant/TenantSite.jsx's
 *     own `admin` route serves the same `/admin/*` on a tenant host (the Host names the
 *     practice there);
 *   - App.js `/t/:slug/*` → TenantSite → nested `admin` → `/t/{slug}/admin/*`.
 *
 * In the second form the practice is named by the PATH, and lib/api.js reads it from
 * there (`getCurrentTenantSlug`). A link written as a bare "/admin/..." drops that
 * prefix, leaves the `/t/:slug` tree for the platform `/admin` tree, and hands the choice
 * of practice to the remembered bare-console slug (lib/api.js) — which another tab may have
 * rewritten. So every in-console destination is written in its CANONICAL spelling
 * ("/admin", "/admin/payments", …: the platform tree's path, greppable and identical to
 * the route tables) and re-rooted onto the console base of the current address.
 *
 * The base is read from `window.location`, the same source lib/api.js picks the request
 * tenant from, so a link and the requests of the page it opens cannot disagree about the
 * practice. It is constant for a mounted console: moving between `/admin` and
 * `/t/{slug}/admin` switches route trees, which remounts everything built from it.
 *
 * Re-rooting replaces the leading "/admin" segment; it never prepends to an arbitrary
 * string, so a destination cannot come out double-prefixed.
 */

const CANONICAL_BASE = "/admin";

// Same slug grammar as lib/api.js `getCurrentTenantSlug` and lib/tenantPath.js, anchored
// on the console segment: `/t/{slug}/admin` or `/t/{slug}/admin/...`, nothing else.
const PREFIXED_CONSOLE = /^\/t\/([a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])?)\/admin(?=[/?#]|$)/;

/**
 * The console base of a pathname: "/t/{slug}/admin" under the path prefix, "/admin"
 * everywhere else (platform `/admin/*`, and tenant hosts where the Host names the practice).
 *   "/t/mkttest-05/admin/payments" → "/t/mkttest-05/admin"
 *   "/admin/payments"              → "/admin"
 */
export const adminBasePath = (pathname) => {
  const m = (pathname || "").match(PREFIXED_CONSOLE);
  return m ? `/t/${m[1]}${CANONICAL_BASE}` : CANONICAL_BASE;
};

/**
 * A canonical console destination re-rooted onto the console base of the current address.
 *   at /t/x/admin/payments: adminHref("/admin/billing") → "/t/x/admin/billing"
 *   at /admin/payments:     adminHref("/admin")         → "/admin"
 * Anything that is not a canonical console path is a programming error, not a link to
 * pass through: returning it unchanged would reintroduce exactly the prefix drop above.
 */
export const adminHref = (canonical) => {
  const rest = typeof canonical === "string" && canonical.startsWith(CANONICAL_BASE)
    ? canonical.slice(CANONICAL_BASE.length)
    : null;
  if (rest === null || !(rest === "" || /^[/?#]/.test(rest))) {
    throw new Error(`adminHref: "${canonical}" is not a canonical console path ("/admin" or "/admin/...")`);
  }
  const pathname = typeof window === "undefined" ? "" : window.location.pathname;
  return `${adminBasePath(pathname)}${rest}`;
};

/**
 * Signing in RETURNS TO THE PAGE THAT ASKED FOR IT.
 *
 * A signed-out visit to a console page (a link in an email: the practitioner's
 * confirmation carries `/admin/telehealth/consult/<id>`, the message notice
 * `/admin/messages?c=<id>`) is redirected to the login page. Without a return target the
 * sign-in then lands on the dashboard home and the link the practitioner followed is lost.
 * ProtectedRoute therefore names the page in `?next=` (`adminLoginHref`), and Login goes
 * back to it (`consoleReturnTarget`) instead of `/admin`. A query parameter rather than
 * router state, because the tenant-host sign-in leaves the page for the auth bridge and
 * comes back to the same login URL: router state does not survive that round trip, the
 * query string does.
 */
export const LOGIN_RETURN_PARAM = "next";

/**
 * The login page of the current console, naming `returnTo` (pathname + search + hash of
 * the page being protected) when it is a console page worth returning to.
 *   at /t/x/admin/telehealth/consult/a1: adminLoginHref("/t/x/admin/telehealth/consult/a1")
 *     → "/t/x/admin/login?next=%2Ft%2Fx%2Fadmin%2Ftelehealth%2Fconsult%2Fa1"
 *   at /admin: adminLoginHref("/admin") → "/admin/login" (the default destination anyway)
 */
export const adminLoginHref = (returnTo) => {
  const login = adminHref("/admin/login");
  const target = consoleReturnTarget(returnTo);
  if (!target || target === adminHref("/admin")) return login;
  return `${login}?${LOGIN_RETURN_PARAM}=${encodeURIComponent(target)}`;
};

/**
 * A path segment that a URL parser or the router would treat as `.` or `..` — including its
 * percent-encoded forms (`%2e%2e`, and the double-encoded `%252e%252e` a query value decodes
 * into). The segment is decoded until it stops changing so that no encoding depth slips
 * through; a segment that cannot be decoded is treated as hostile.
 */
const isDotSegment = (seg) => {
  let cur = seg;
  for (let i = 0; i < 8; i += 1) {
    if (cur === "." || cur === "..") return true;
    let next;
    try {
      next = decodeURIComponent(cur);
    } catch {
      return true;
    }
    if (next === cur) return false;
    cur = next;
  }
  return true;
};

/**
 * `next` if it is a page of THIS console, else null (the caller falls back to `/admin`).
 *
 * Accepted only when it is a same-origin path inside the current console base (so a
 * `/t/{slug}/admin` sign-in cannot be sent into another practice's tree or out of the
 * console), is not the login page itself, and carries nothing a router could resolve
 * elsewhere: no scheme or protocol-relative `//`, no backslash, no `.`/`..` segment in any percent-encoding.
 * Anything else is dropped rather than repaired — the parameter is attacker-writable.
 */
export const consoleReturnTarget = (next) => {
  if (typeof next !== "string" || !next.startsWith("/")) return null;
  if (next.startsWith("//") || next.includes("\\")) return null;
  const base = adminBasePath(typeof window === "undefined" ? "" : window.location.pathname);
  if (!next.startsWith(base)) return null;
  if (next !== base && !/^[/?#]/.test(next.slice(base.length))) return null;
  const path = next.split(/[?#]/)[0];
  if (path.split("/").some(isDotSegment)) return null;
  if (path === `${base}/login` || path.startsWith(`${base}/login/`)) return null;
  return next;
};
